/**
 * Splits a database open into what must be retrieved BEFORE the viewer opens
 * and what may stream in afterwards.
 *
 * The blocking half is every non-image dataset plus exactly one anchor slice
 * per streamable image series; the streaming half is that series' remaining
 * slices, in the manifest's z order.
 *
 * **Graceful degradation is the load-bearing property here.** Every failure of
 * the progressive preparation — no dataset id, no manifest, an unparseable
 * manifest, an inventory the source is still filling (a C-STORE push in
 * flight), geometry that cannot form a uniform stack — resolves to "this
 * series downloads in full, exactly as it does today". The worst case of the
 * whole feature is the behaviour that shipped before it.
 *
 * PHI: manifest failures are logged at debug level with no identifiers, never
 * a SOP/Series/Study Instance UID and never a patient id.
 */

import type { ImageDetailResponse } from '~/backend/connector-next-api/connector-next-schemas';
import { fetchImagesGetById } from '~/backend/connector-next-api/connector-next-components';
import {
    isAbortError,
    type InstanceAddress,
} from '~/backend/connector-next-api/dataRetrieval';
import { withRequestDeadline } from '~/backend/connector-next-api/dataRetrieval';
import { toImageSeriesManifest } from '~/backend/connector-next-api/imageManifest';
import { isImageModality } from '~/domain/dicom/modality';
import {
    SliceValidationStatus,
    validateSlicePositions,
} from '~/domain/patient/image3d';
import type { ImageSeriesManifest } from '~/domain/patient/imageSeriesManifest';
import type { ResolvedSeries } from './resolveOpenTargets';

/**
 * One image series that will be built from its manifest and filled in after
 * the viewer opens.
 */
export type SeriesStreamPlan = {
    /** Full geometry and slice inventory of the series. */
    manifest: ImageSeriesManifest;
    /**
     * The one slice the blocking phase retrieves. `Image3D.fromManifest`
     * cannot build a volume without a parsed slice, so this is a hard
     * precondition of the session, not an optimisation.
     */
    anchorSopInstanceUid: string;
    /**
     * Every other slice, in manifest (z ascending) order — the order they are
     * fetched in.
     */
    remaining: InstanceAddress[];
};

/**
 * Streamable plans and those that have to fall back, after the blocking phase
 * reported which anchors it could not retrieve.
 */
export type AnchorPartition = {
    /** Anchor in hand: these stream as planned. */
    streamable: SeriesStreamPlan[];
    /**
     * Anchor lost. A series cannot start without one, and dropping the plan
     * would silently discard the whole series — so it is retrieved in full
     * instead, which is the behaviour that shipped before streaming existed.
     */
    recover: SeriesStreamPlan[];
};

/** The blocking/streaming split of one open. */
export type OpenDownloadPlan = {
    /** Instances downloaded before the viewer opens, in study/series order. */
    blocking: InstanceAddress[];
    /** Series whose pixels arrive after the open; empty means "as today". */
    streaming: SeriesStreamPlan[];
};

export type StreamingPlanOptions = {
    signal?: AbortSignal;
    /** Injectable for tests; defaults to `GET /api/images/{id}`. */
    fetchManifest?: (
        datasetId: string,
        signal?: AbortSignal
    ) => Promise<ImageDetailResponse>;
};

/**
 * How long a manifest request may take before the series is planned as a full
 * download instead. Without it a connector that accepts the request and never
 * answers holds the open in planning forever, and the documented fallback —
 * every manifest failure degrades to a blocking download — is never reached.
 */
const MANIFEST_DEADLINE_MS = 30_000;

function defaultFetchManifest(
    datasetId: string,
    signal?: AbortSignal
): Promise<ImageDetailResponse> {
    return withRequestDeadline(signal, MANIFEST_DEADLINE_MS, (deadline) =>
        fetchImagesGetById({ pathParams: { id: datasetId } }, deadline)
    );
}

/**
 * Whether a series is even worth asking the manifest endpoint about.
 *
 * A single-instance series is excluded deliberately: it cannot form a 3D
 * volume, so streaming has nothing to add and the blocking path already
 * retrieves it in one request.
 */
function isStreamCandidate(series: ResolvedSeries): boolean {
    return (
        series.datasetId !== undefined &&
        series.datasetId !== '' &&
        isImageModality(series.modality) &&
        series.isValid3D &&
        series.instances.length > 1
    );
}

/**
 * Whether a volume can be built from this manifest alone.
 *
 * Runs the SAME validation `Image3D.fromManifest` runs, over the SAME
 * inventory, so a manifest accepted here is one the domain accepts at session
 * creation. What it deliberately cannot pre-check is the anchor slice (not yet
 * parsed) and the grid's per-slice z mapping; `dicomProcessor` catches those
 * and falls back to the parsed-series path rather than failing the open.
 */
function canBuildVolume(manifest: ImageSeriesManifest): boolean {
    if (!manifest.isInventoryComplete) {
        // The source is still receiving the series (daemon-mode C-STORE mid
        // push): a grid sized to a partial inventory is wrong for every slice
        // that arrives later.
        return false;
    }
    const validation = validateSlicePositions(
        manifest.toSliceGeometryInventory()
    );
    return validation.status === SliceValidationStatus.CompleteReconstruction;
}

/**
 * The anchor is the middle slice of the manifest's z-ordered inventory: the
 * one the viewer opens on, and the cheapest single slice that makes the volume
 * constructible.
 */
export function anchorIndexFor(sliceCount: number): number {
    return Math.floor(sliceCount / 2);
}

/**
 * Plans one series, or `undefined` when it must fall back to a full download.
 *
 * @throws the caller's AbortError, and only that: every other failure is a
 *   fallback, not an error.
 */
async function planSeries(
    series: ResolvedSeries,
    fetchManifest: NonNullable<StreamingPlanOptions['fetchManifest']>,
    signal?: AbortSignal
): Promise<SeriesStreamPlan | undefined> {
    let manifest: ImageSeriesManifest;
    try {
        manifest = toImageSeriesManifest(
            await fetchManifest(series.datasetId as string, signal)
        );
    } catch (error) {
        if (isAbortError(error) || signal?.aborted) {
            throw error;
        }
        // Names nothing: this line reaches the browser console.
        console.debug(
            'Image manifest unavailable or unusable; the series will be ' +
                'downloaded in full'
        );
        return undefined;
    }

    if (manifest.seriesInstanceUid !== series.seriesInstanceUid) {
        // The dataset id addressed a different series than the detail listed;
        // building this patient's volume from it would be a wrong volume.
        console.debug(
            'Image manifest describes a different series than requested; ' +
                'the series will be downloaded in full'
        );
        return undefined;
    }
    if (!canBuildVolume(manifest)) {
        console.debug(
            'Image manifest does not describe a complete, uniform slice ' +
                'stack; the series will be downloaded in full'
        );
        return undefined;
    }

    const anchorIndex = anchorIndexFor(manifest.sliceCount);
    const anchorSopInstanceUid = manifest.slices[anchorIndex].sopInstanceUid;
    const remaining = manifest.slices
        .filter((slice) => slice.sopInstanceUid !== anchorSopInstanceUid)
        .map((slice) => ({
            studyInstanceUid: series.studyInstanceUid,
            seriesInstanceUid: series.seriesInstanceUid,
            sopInstanceUid: slice.sopInstanceUid,
        }));

    return { manifest, anchorSopInstanceUid, remaining };
}

/**
 * Builds the blocking/streaming split for a resolved selection.
 *
 * The blocking list keeps the resolved study/series order, so an open with
 * nothing streamable produces exactly the list the flat resolution produced.
 *
 * @throws only the caller's AbortError; every other failure degrades to a
 *   full blocking download of the series it happened for.
 */
export async function planProgressiveOpen(
    series: readonly ResolvedSeries[],
    options: StreamingPlanOptions = {}
): Promise<OpenDownloadPlan> {
    const fetchManifest = options.fetchManifest ?? defaultFetchManifest;

    // One manifest request per candidate series, issued together: they are
    // independent, and the open cannot start until the last of them answers,
    // so serialising them adds a round trip per series to the wait before
    // anything appears. Results are consumed in the order the series were
    // given, which decides download order.
    const plans = await Promise.all(
        series.map((candidate) =>
            isStreamCandidate(candidate)
                ? planSeries(candidate, fetchManifest, options.signal)
                : Promise.resolve(undefined)
        )
    );

    const blocking: InstanceAddress[] = [];
    const streaming: SeriesStreamPlan[] = [];
    for (const [index, candidate] of series.entries()) {
        const plan = plans[index];
        if (!plan) {
            blocking.push(...candidate.instances);
            continue;
        }
        blocking.push({
            studyInstanceUid: candidate.studyInstanceUid,
            seriesInstanceUid: candidate.seriesInstanceUid,
            sopInstanceUid: plan.anchorSopInstanceUid,
        });
        streaming.push(plan);
    }

    return { blocking, streaming };
}

/**
 * Drops the plans whose anchor the blocking download did not obtain.
 *
 * Without a parsed anchor there is no volume to fill, so streaming its
 * remaining slices would fetch pixels nothing can hold. The shortfall is
 * already counted in the retrieval confirmation the user answered.
 */
export function partitionPlansByAnchor(
    streaming: readonly SeriesStreamPlan[],
    skipped: readonly InstanceAddress[]
): AnchorPartition {
    if (skipped.length === 0) {
        return { streamable: [...streaming], recover: [] };
    }
    const missing = new Set(skipped.map((address) => address.sopInstanceUid));
    const streamable: SeriesStreamPlan[] = [];
    const recover: SeriesStreamPlan[] = [];
    for (const plan of streaming) {
        if (missing.has(plan.anchorSopInstanceUid)) {
            recover.push(plan);
        } else {
            streamable.push(plan);
        }
    }
    return { streamable, recover };
}

/**
 * Every instance of a series, in manifest order — the address list for a
 * series that has to be retrieved the blocking way after all.
 *
 * `remaining` is every slice except the anchor, so the anchor is put back to
 * make the list whole. Built from the manifest rather than from `remaining`
 * so the result does not depend on which slice was chosen as the anchor.
 */
export function allInstancesOf(plan: SeriesStreamPlan): InstanceAddress[] {
    return plan.manifest.slices.map((slice) => ({
        studyInstanceUid: plan.manifest.studyInstanceUid,
        seriesInstanceUid: plan.manifest.seriesInstanceUid,
        sopInstanceUid: slice.sopInstanceUid,
    }));
}

/** Manifests keyed by Series Instance UID, as the DICOM commit consumes them. */
export function manifestsBySeriesUid(
    streaming: readonly SeriesStreamPlan[]
): Map<string, ImageSeriesManifest> {
    return new Map(
        streaming.map((plan) => [
            plan.manifest.seriesInstanceUid,
            plan.manifest,
        ])
    );
}
